import type { ReactNode } from 'react'
import { Link } from 'wouter'
import { ExternalLink, FolderOpen, Plug } from 'lucide-react'
import {
  Badge,
  Chip,
  humanize,
  CopyButton,
  EmptyState,
  DetailList,
  Section,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  FormattedDate,
  TextLink,
} from '@ideon-api/ideon-ui'
import type { AccountDetail } from '../../lib/use-account-detail'
import { useMachineUsers } from '../../lib/use-service-accounts'
import { MachineUsersSection } from '../machine-users/MachineUsersSection'
import { setHref } from '../../lib/account-links'

interface OverviewTabProps {
  slug: string
  data: AccountDetail
}

// adminUrl comes from the API; refuse anything but http(s) so it cannot render a javascript: link.
const isHttpUrl = (url: string | null | undefined): url is string => !!url && /^https?:\/\//i.test(url)

const SUBHEADING = 'font-manrope text-sm font-semibold text-ideon-gray-black dark:text-ideon-gray-30'

const chips = (items: string[]) =>
  items.length ? (
    <div className="flex flex-wrap gap-1">
      {items.map((item) => (
        <Chip key={item} variant="outlined">
          {item}
        </Chip>
      ))}
    </div>
  ) : (
    '—'
  )

// Every row reserves the copy button's slot, so values share one right edge with or without a copy.
const withCopySlot = (value: ReactNode, copy?: string, label?: string) => (
  <span className="flex items-center justify-end gap-2">
    {value}
    <span className="flex w-12 justify-end">
      {copy && <CopyButton value={copy} label="Copy" aria-label={label} variant="text" size="xs" className="px-1" />}
    </span>
  </span>
)

export function OverviewTab({ slug, data }: OverviewTabProps) {
  // One list feeds both the badge and the section, so create/revoke reload updates both.
  const machineUsers = useMachineUsers(slug)
  return (
    <div className="flex flex-col gap-6">
      <Section heading="Account info" density="compact" bordered>
        <DetailList.Root>
          <DetailList.Item label="Name" value={withCopySlot(data.summary?.name ?? '—')} data-testid="detail-name" />
          <DetailList.Item label="Slug" value={withCopySlot(data.summary?.slug ?? '—')} data-testid="detail-slug" />
          <DetailList.Item
            label="ID"
            value={
              data.summary?.id
                ? withCopySlot(<code className="font-mono text-xs">{data.summary.id}</code>, data.summary.id, 'Copy account id')
                : withCopySlot('—')
            }
            data-testid="detail-id"
          />
          <DetailList.Item
            label="Created"
            value={withCopySlot(data.summary?.createdAt ? <FormattedDate value={data.summary.createdAt} /> : '—')}
            data-testid="detail-created"
          />
        </DetailList.Root>
      </Section>

      <Section heading="Permission sets" density="compact" bordered>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Permission set</TableHead>
              <TableHead>Licensed</TableHead>
              <TableHead>Apps</TableHead>
              <TableHead>Features</TableHead>
              <TableHead>Adapters</TableHead>
              <TableHead>Slack</TableHead>
              <TableHead>Bulk files</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.permissionSets.map((set) => {
              const adapters = set.adapters
              // Unknown until the list is ready: flagging on a loading or failed fetch would claim "no machine users".
              const noDelivery =
                machineUsers.status === 'ready' &&
                adapters.length === 0 &&
                set.applicationCount === 0 &&
                !machineUsers.data.some((m) => m.permissionSetKey === set.key)
              return (
                <TableRow key={set.key}>
                  <TableCell className="font-semibold">
                    <TextLink asChild><Link href={setHref(slug, 'licensing', set.key)}>{set.key}</Link></TextLink>
                    {set.default && (
                      <Badge variant="neutral" uppercase={false} dot={false} className="ml-2 bg-transparent">
                        Default
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="flex flex-wrap items-center gap-2">
                      <Badge variant={set.licensedInCurrentPeriod ? 'success' : 'neutral'} uppercase={false} dot={false}>
                        {set.licensedInCurrentPeriod ? 'Licensed' : 'Not licensed'}
                      </Badge>
                      {noDelivery && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="warning" uppercase={false} dot={false} tabIndex={0}>
                              No data delivery
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            No data delivery: no bulk adapters, API apps or machine users
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>{set.applicationCount}</TableCell>
                  <TableCell>{chips(set.featuresEnabled)}</TableCell>
                  <TableCell>{adapters.length ? adapters.join(', ') : '—'}</TableCell>
                  <TableCell>{set.slackChannel ?? '—'}</TableCell>
                  <TableCell>
                    <TextLink asChild>
                      <Link href={setHref(slug, 'files', set.key)}>
                        <FolderOpen className="h-4 w-4" aria-hidden="true" />
                        Files
                      </Link>
                    </TextLink>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Section>

      <Section heading="API access" density="compact" bordered>
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-3">
            <h3 className={SUBHEADING}>3scale applications</h3>
            {data.threeScaleApplications.length === 0 ? (
              <EmptyState
                icon={<Plug className="h-10 w-10" />}
                heading="No 3scale applications"
                description="A 3scale application is an API key pair this account uses to call Ideon APIs."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Application ID</TableHead>
                    <TableHead>Permission set</TableHead>
                    <TableHead>Environment</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.threeScaleApplications.map((application) => (
                    <TableRow key={`${application.environment}:${application.applicationId}`}>
                      <TableCell>
                        <code className="font-mono text-xs">{application.applicationId}</code>
                      </TableCell>
                      <TableCell>{application.permissionSetKey ?? '—'}</TableCell>
                      <TableCell>{humanize(application.environment)}</TableCell>
                      <TableCell>
                        <span className="flex items-center justify-end gap-3">
                          <CopyButton
                            value={application.applicationId}
                            label="Copy"
                            aria-label={`Copy application id ${application.applicationId}`}
                            variant="text"
                            size="xs"
                            className="px-1"
                          />
                          {isHttpUrl(application.adminUrl) && (
                            <TextLink
                              href={application.adminUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="whitespace-nowrap text-xs font-medium"
                            >
                              Open in 3scale
                              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                            </TextLink>
                          )}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <MachineUsersSection slug={slug} state={machineUsers} />
        </div>
      </Section>

    </div>
  )
}
